import {Link} from 'react-router-dom';
import {artistPageTabs} from '@app/web-player/artists/artist-page-tabs';
import {Tabs} from '@ui/tabs/tabs';
import {TabList} from '@ui/tabs/tab-list';
import {Tab} from '@ui/tabs/tab';
import {Trans} from '@ui/i18n/trans';
import {TabPanel, TabPanels} from '@ui/tabs/tab-panels';
import {DiscographyTab} from '@app/web-player/artists/artist-page/discography-panel/discography-tab';
import {SimilarArtistsPanel} from '@app/web-player/artists/artist-page/similar-artists-panel';
import {ArtistAboutPanel} from '@app/web-player/artists/artist-page/artist-about-panel';
import {ArtistTracksPanel} from '@app/web-player/artists/artist-page/artist-tracks-panel';
import {ArtistAlbumsPanel} from '@app/web-player/artists/artist-page/artist-albums-panel';
import React from 'react';
import {UseArtistResponse} from '@app/web-player/artists/requests/use-artist';
import {useArtistPageTabs} from '@app/web-player/artists/artist-page/use-artist-page-tabs';
import {ArtistFollowersPanel} from '@app/web-player/artists/artist-page/artist-followers-panel';
import { ArtistMediasPanel } from './artist-medias-panel';
import { useAlbumTaxonomiesContext } from '@common/admin/album-taxonomies-context';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { getTabLabel } from '@app/web-player/artists/artist-label';
import clsx from 'clsx';

interface Props {
  data: UseArtistResponse;
}
export function ArtistPageTabs({data}: Props) {
  const {selectedIndex, activeTabs} = useArtistPageTabs(data.artist);
  const { isLoading  } = useAlbumTaxonomiesContext();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-24 py-20">
        <ProgressCircle aria-label="Loading content..." isIndeterminate />
      </div>
    );
  }
  
  return (
    <Tabs className="mt-24 md:mt-48" selectedTab={selectedIndex} isLazy>
      <TabList className={clsx(
        "mt-12 md:mt-24",
        activeTabs.length === 1 ? 'hidden' : null
      )}>
        {activeTabs.map(tab => {
          switch (`${tab.id}`) {
            case artistPageTabs.discography:
              return (
                <Tab elementType={Link} to="" key={artistPageTabs.discography}>
                  <Trans message={getTabLabel("discography", data.artist.type)} />
                </Tab>
              );
            case artistPageTabs.similar:
              return (
                <Tab
                  elementType={Link}
                  to={{search: '?tab=similar'}}
                  key={artistPageTabs.similar}
                >
                  <Trans message="Similar artists" />
                </Tab>
              );
            case artistPageTabs.about:
              return (
                <Tab
                  elementType={Link}
                  to={{search: '?tab=about'}}
                  key={artistPageTabs.about}
                >
                  <Trans message="About" />
                </Tab>
              );
            case artistPageTabs.tracks:
              return (
                <Tab
                  elementType={Link}
                  to={{search: `?tab=${getTabLabel("tracks", data.artist.type).toLowerCase()}`}}
                  key={artistPageTabs.tracks}
                >
                  <Trans message={getTabLabel("tracks", data.artist.type)} />
                </Tab>
              );
            case artistPageTabs.albums:
              return (
                <Tab
                  elementType={Link}
                  to={{search: `?tab=${getTabLabel("albums", data.artist.type).toLowerCase()}`}}
                  key={artistPageTabs.albums}
                >
                  <Trans message={getTabLabel("albums", data.artist.type)} />
                </Tab>
              );
            case artistPageTabs.followers:
              return (
                <Tab
                  elementType={Link}
                  to={{search: '?tab=followers'}}
                  key={artistPageTabs.followers}
                >
                  <Trans message="Followers" />
                </Tab>
              );
          }
        })}
      </TabList>
      
      <TabPanels className="mt-12 md:mt-24">
        {activeTabs.map(tab => {
          switch (`${tab.id}`) {
            case artistPageTabs.discography:
              return (
                <TabPanel key={artistPageTabs.discography}>
                  <DiscographyTab data={data} />
                </TabPanel>
              );
            case artistPageTabs.similar:
              return (
                <TabPanel key={artistPageTabs.similar}>
                  <SimilarArtistsPanel artist={data.artist} />
                </TabPanel>
              );
            case artistPageTabs.about:
              return (
                <TabPanel key={artistPageTabs.about}>
                  <ArtistAboutPanel artist={data.artist} />
                </TabPanel>
              );
            case artistPageTabs.tracks:
              return (
                <TabPanel key={artistPageTabs.tracks}>
                  <ArtistTracksPanel
                    artist={data.artist}
                    initialTracks={data.tracks}
                  />
                </TabPanel>
              );
            case artistPageTabs.albums:
              return (
                <TabPanel key={artistPageTabs.albums}>
                  <ArtistMediasPanel artist={data.artist} />
                </TabPanel>
              );
            case artistPageTabs.followers:
              return (
                <TabPanel key={artistPageTabs.followers}>
                  <ArtistFollowersPanel artist={data.artist} />
                </TabPanel>
              );
          }
        })}
      </TabPanels>
    </Tabs>
  );
}
